import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  // TextField,
  // InputAdornment,
} from '@mui/material';
// import SearchIcon from '@mui/icons-material/Search';
import { MVPFiltersProps } from '../../advanta-mvp-charts/constants';
import { MVPDataTableInternal } from '../components/table';
import CommonAppBarAdmin from '../../../components/commonAppBarAdmin';
import {
  useGetImportFileHistoryQuery,
  useLazyGetTableCSVPresignedURLByTableTypeQuery,
} from '../../../services/mVPGraphQlService';
import {
  TFieldReport,
  TImportHistory,
  TRequestTableDataByTableType,
} from '../../../types/mvpCharts';
import dayjs from 'dayjs';
import StatusPill from '../components/statusPill';
import arrow from '../../../assets/icons/stash_arrow-up.svg';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

export const ImportHistory: React.FC<MVPFiltersProps> = ({
  onBack,
  type: type,
}) => {
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<
    TFieldReport[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [allTableData, setAllTableData] = useState<
    TFieldReport[]
  >([]);

  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const queryParams: TRequestTableDataByTableType = {
    pageNumber: pageNo,
    pageSize: pageSize,
    tableType: type == 'user' ? 'TT_USER' : 'TT_GEOGRAPHY',
    wsId: wsId,
  };
  const queryParamscsv: TRequestTableDataByTableType = {
    tableType: type == 'user' ? 'TT_USER' : 'TT_GEOGRAPHY',
    wsId: wsId,
  };

  const [getCSVUrl] =
    useLazyGetTableCSVPresignedURLByTableTypeQuery();

  const handleDownloadTemplate = async (url) => {
    try {
      const response = await getCSVUrl({
        ...queryParamscsv,
        fileKey: url,
      }).unwrap();

      const downloadUrl =
        response?.data?.GetTableCSVPresignedURLByTableType
          ?.fileUploads?.[0]?.fileUrl;

      if (!downloadUrl) {
        throw new Error('Download URL not found');
      }

      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Download template failed', err);
    }
  };

  const {
    data: getTableData,
    isLoading,
    isFetching,
  } = useGetImportFileHistoryQuery(queryParams, {
    refetchOnMountOrArgChange: true, //change
  });

  useEffect(() => {
    if (getTableData?.data?.GetImportFileHistory?.records) {
      setTotalCount(
        getTableData?.data?.GetImportFileHistory
          ?.totalCount || 0
      );
      setTableData(
        getTableData?.data?.GetImportFileHistory?.records
      );

      setAllTableData(
        getTableData?.data?.GetImportFileHistory?.records
      );
    }
  }, [getTableData]);

  const handlePageChange = (
    event: React.MouseEvent | null,
    newPageNumber: number
  ) => {
    setPageNo(newPageNumber);
    setTableData(
      getPaginatedData(
        newPageNumber,
        pageSize,
        allTableData
      )
    );
  };

  const getPaginatedData = (
    newPageNumber: number,
    itemsPerPage: number,
    allItemsList: TFieldReport[]
  ) => {
    const indexOfLastItem =
      (newPageNumber + 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPageItems = allItemsList?.slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    return [...currentPageItems];
  };

  const data = tableData;
  type Column<T, K extends keyof T> = {
    key: K;
    label: string;
    cellModifier?: (data: T) => React.ReactNode | string;
  };

  const getFileNameBeforeUnderscore = (
    fileKey?: string
  ) => {
    if (!fileKey) return '--';
    return fileKey.split('_')[0];
  };

  const columns: Column<
    TImportHistory,
    keyof TImportHistory
  >[] = [
    {
      key: 'importId',
      label: 'Imported ID',
      cellModifier(data) {
        return data?.importId;
      },
    },

    {
      key: 'file',
      label: 'File Name',
      cellModifier(data) {
        const fileKey = data?.file?.fileKey;
        const displayName =
          getFileNameBeforeUnderscore(fileKey);

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Box fontWeight={500}>{displayName}</Box>

            {fileKey && (
              <Box
                component="button"
                onClick={() =>
                  handleDownloadTemplate(
                    data?.file?.fileUrl
                  )
                }
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}>
                <img
                  src={arrow}
                  alt="close"
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </Box>
            )}
          </Box>
        );
      },
    },

    {
      key: 'importedBy',
      label: 'Imported On',
      cellModifier(data) {
        return dayjs(
          data?.importedBy?.actorTime?.seconds
            ? data?.importedBy?.actorTime?.seconds * 1000
            : 0
        ).format('DD/MM/YYYY');
      },
    },
    {
      key: 'importedBy',
      label: 'Imported By',
      cellModifier: (data) => {
        return data?.importedBy?.actorId?.actorName;
      },
    },

    {
      key: 'totalRecords',
      label: 'Total Records',
      cellModifier: (data) => {
        return data?.totalRecords;
      },
    },
    {
      key: 'successRecords',
      label: 'Success',
      cellModifier: (data) => {
        return data?.successRecords;
      },
    },
    {
      key: 'failedRecords',
      label: 'Failed',
      cellModifier: (data) => {
        return data?.failedRecords;
      },
    },

    {
      key: 'status',
      label: 'Status',
      cellModifier: (data) => {
        return <StatusPill status={data?.status} />;
      },
    },
    {
      key: 'errorFileUrl',
      label: 'Error Log',
      cellModifier: (data) => {
        const errorUrl = data?.errorFileUrl;

        if (!errorUrl) {
          return '--';
        }

        return (
          <Box
            component="button"
            onClick={() => handleDownloadTemplate(errorUrl)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #007a7a',
              borderRadius: '8px',
              padding: '6px 12px',
              cursor: 'pointer',
              backgroundColor: '#fff',
              color: '#007a7a',
              fontWeight: 500,
            }}>
            <ArrowDownwardIcon
              sx={{ height: 20, width: 20 }}
            />
            Download
          </Box>
        );
      },
    },
  ];

  return (
    <div>
      {
        <CommonAppBarAdmin
          height={'auto'}
          title={'Import history'}
          onBack={onBack}
          section={
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                {/* <TextField
                  placeholder="Search by field number or lot number"
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: 260,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

              */}
              </Box>
            </>
          }
        />
      }
      <div style={{ marginTop: 100 }}></div>

      {isLoading || isFetching ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="60vh">
          <CircularProgress />
        </Box>
      ) : (
        <MVPDataTableInternal
          columns={columns}
          data={data?.length ? data : []}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalCount}
          pageNo={pageNo}
          setPageNo={setPageNo}
          isLoading={false}
          rowsPerPageOptions={[pageSize]}
          handlePageChange={handlePageChange}
        />
      )}
    </div>
  );
};
